import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import { ASSET, APPLY_LOGIN, renewalPath } from 'components/Renewal/paths'
import { INDUSTRIES, industryIcon } from 'components/Renewal/nav'
import usePostSlackMessageSend from 'hooks/query/slackMessageSend/usePostSlackMessageSend'

const WORDS = ['청소', '상품 진열', '소모품 보충', '기기 점검']

const SLIDES = [
  { src: `${ASSET}/photos/hero-285.webp`, alt: '크루가 무인 세탁함 화면을 휴대폰으로 촬영하는 모습', pos: 'center 40%' },
  { src: `${ASSET}/photos/hero-293-w.webp`, alt: '크루가 무인문구점 진열대에 상품을 채우는 모습', pos: 'center 40%' },
  { src: `${ASSET}/photos/hero-292.webp`, alt: '크루가 스터디카페 의자를 정리하는 모습', pos: 'center 40%' },
  { src: `${ASSET}/photos/hero-291.webp`, alt: '크루가 무인카페 머신에 컵을 보충하는 모습', pos: 'center 35%' },
  { src: `${ASSET}/photos/hero-290.webp`, alt: '크루가 자판기에 상품을 채우는 모습', pos: 'center 40%' },
  { src: `${ASSET}/photos/hero-289.webp`, alt: '크루가 세탁기 문 안쪽을 닦는 모습', pos: 'center 45%' },
  { src: `${ASSET}/photos/hero-288.webp`, alt: '크루가 아이스크림 냉동고를 정리하는 모습', pos: 'center 50%' },
  { src: `${ASSET}/photos/hero-287.webp`, alt: '크루가 사진 인화기 리본을 교체하는 모습', pos: 'center 45%' },
]

const JUMPS = [
  { href: '#scope', label: '관리 범위' },
  { href: '#report', label: '완료 보고' },
  { href: '#field', label: '관리 현장' },
  { href: '#stories', label: '사장님 이야기' },
  { href: '#price', label: '요금' },
  { href: '#start', label: '시작하는 방법' },
  { href: '#area', label: '서비스 지역' },
  { href: '#apply', label: '간편 상담' },
]

const CASES = [
  {
    id: 'laundry',
    chip: '빨래방',
    label: '셀프빨래방 관리 보고 보기',
    caption: '셀프빨래방 · 건조기 먼지 제거',
    before: `${ASSET}/photos/ba-laundry-before.jpg`,
    after: `${ASSET}/photos/ba-laundry-after.jpg`,
    beforeAlt: '관리 전: 건조기 안에 먼지가 두껍게 쌓인 모습',
    afterAlt: '관리 후: 먼지를 걷어 내 깨끗해진 건조기 내부',
  },
  {
    id: 'store',
    chip: '편의점',
    label: '무인편의점 관리 보고 보기',
    caption: '무인편의점 · 쓰레기통과 매대 정리',
    before: `${ASSET}/photos/ba-store-before.jpg`,
    after: `${ASSET}/photos/ba-store-after.jpg`,
    beforeAlt: '관리 전: 쓰레기가 넘치고 박스가 쌓인 계산대 아래',
    afterAlt: '관리 후: 봉투를 새로 끼우고 정리한 쓰레기통과 계산대',
  },
  {
    id: 'photo',
    chip: '사진관',
    label: '무인사진관 관리 보고 보기',
    caption: '무인사진관 · 소품 정리',
    before: `${ASSET}/photos/ba-photo-before.jpg`,
    after: `${ASSET}/photos/ba-photo-after.jpg`,
    beforeAlt: '관리 전: 인형 소품이 선반에 흐트러진 모습',
    afterAlt: '관리 후: 소품을 종류별로 가지런히 정리한 선반',
  },
  {
    id: 'stationery',
    chip: '문구점',
    label: '무인문구점 관리 보고 보기',
    caption: '무인문구점 · 상품 진열',
    before: `${ASSET}/photos/ba-stationery-before-w.jpg`,
    after: `${ASSET}/photos/ba-stationery-after-w.jpg`,
    beforeAlt: '관리 전: 상품이 비고 흐트러진 진열대',
    afterAlt: '관리 후: 상품을 앞줄까지 채워 정리한 진열대',
  },
]

export default function RenewalHomeHero() {
  const [slide, setSlide] = useState(0)
  const [caseId, setCaseId] = useState(CASES[0].id)
  const [brochureOpen, setBrochureOpen] = useState(false)
  const reduceMotion = usePrefersReducedMotion()
  const wordsRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (reduceMotion) return
    const timer = window.setInterval(() => setSlide((value) => (value + 1) % SLIDES.length), 5200)
    return () => window.clearInterval(timer)
  }, [reduceMotion])

  useEffect(() => {
    const host = wordsRef.current
    if (!host) return
    const words = Array.from(host.children) as HTMLElement[]
    if (words.length < 2) return
    const ease = 'cubic-bezier(.2,.8,.2,1)'
    const play = (el: HTMLElement, frames: Keyframe[], options: KeyframeAnimationOptions) => {
      const node = el as HTMLElement & { _wordAnim?: Animation & { commitStyles?: () => void } }
      if (node._wordAnim) {
        try {
          node._wordAnim.commitStyles?.()
        } catch (error) {
          /* 아직 그려지지 않은 애니메이션은 스타일을 확정하지 않는다. */
        }
        node._wordAnim.cancel()
      }
      node._wordAnim = el.animate(frames, options)
    }
    words.forEach((item, index) => {
      item.style.willChange = 'transform, opacity'
      play(
        item,
        [
          { opacity: index ? 0 : 1, transform: index ? 'translateY(100%)' : 'none' },
          { opacity: index ? 0 : 1, transform: index ? 'translateY(100%)' : 'none' },
        ],
        { duration: 1, fill: 'forwards' }
      )
    })
    if (reduceMotion) return
    let index = 0
    const timer = window.setInterval(() => {
      if (document.hidden) return
      const current = words[index]
      const next = (index + 1) % words.length
      play(current, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-90%)' }], {
        duration: 480,
        easing: ease,
        fill: 'forwards',
      })
      play(
        words[next],
        [{ opacity: 0, transform: 'translateY(90%)' }, { opacity: 1, transform: 'none' }],
        { duration: 580, delay: 80, easing: ease, fill: 'forwards' }
      )
      index = next
    }, 2000)
    return () => window.clearInterval(timer)
  }, [reduceMotion])

  const cardFloatRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (reduceMotion) return
    const el = cardFloatRef.current
    if (!el || !window.matchMedia('(pointer: fine)').matches) return
    const depth = -18
    let frame = 0
    let mx = 0
    let my = 0
    let x = 0
    let y = 0
    const tick = () => {
      const tx = -mx * depth
      const ty = -my * depth
      x += (tx - x) * 0.08
      y += (ty - y) * 0.08
      el.style.translate = `${x.toFixed(2)}px ${y.toFixed(2)}px`
      frame = Math.abs(tx - x) > 0.05 || Math.abs(ty - y) > 0.05 ? window.requestAnimationFrame(tick) : 0
    }
    const onMove = (event: PointerEvent) => {
      const root = document.querySelector('.renewal-root')
      if (root && root.scrollTop > window.innerHeight) return
      mx = event.clientX / window.innerWidth - 0.5
      my = event.clientY / window.innerHeight - 0.5
      if (!frame) frame = window.requestAnimationFrame(tick)
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [reduceMotion])

  return (
    <>
      <Hero aria-label="첫 화면">
        <Slides aria-hidden="true">
          {SLIDES.map((item, index) => (
            <Slide key={item.src} src={item.src} alt={item.alt} $on={index === slide} $pos={item.pos} />
          ))}
        </Slides>
        <Shade />
        <Glow aria-hidden="true" />
        <HeroInner>
          <Copy>
            <Eyebrow data-motion="reveal">
              <span>크루가 대신</span>
              <WordPill ref={wordsRef}>
                {WORDS.map((item) => (
                  <Word key={item}>{item}</Word>
                ))}
              </WordPill>
            </Eyebrow>
            <Title>
              <TitleLine>
                <TitleText data-motion="line">무인매장 관리, 이제</TitleText>
              </TitleLine>
              <TitleLine>
                <TitleText data-motion="line" data-delay={110}>
                  <Amber>매일 안 가셔도</Amber> 돼요
                </TitleText>
              </TitleLine>
              <TitleLine>
                <Lead data-motion="line" data-delay={220}>사장님 대신 매장에 가는 브라우니 크루</Lead>
              </TitleLine>
            </Title>
            <Description data-motion="reveal" data-delay={640}>
              정해진 요일에 브라우니 크루가 방문해 청소·상품 진열·소모품 보충·기기 점검을 해요. 끝나면 관리한 사진을 앱으로 보내 드려요.
            </Description>
            <Actions data-motion="reveal" data-delay={720}>
              <Primary to={APPLY_LOGIN}>
                관리 신청하기
                <Arrow />
              </Primary>
              <Ghost to={renewalPath('/apply')}>1:1 상담하기</Ghost>
              <GhostButton type="button" onClick={() => setBrochureOpen(true)}>
                소개서 받기
              </GhostButton>
            </Actions>
            <PriceLink href="#price" data-motion="reveal" data-delay={780}>요금 먼저 보기</PriceLink>
          </Copy>
          <ReportSlot data-motion="reveal" data-delay={500}>
            <ReportFloat ref={cardFloatRef}>
            <Report>
            <Chips role="group" aria-label="업종 고르기">
              {CASES.map((item) => (
                <Chip key={item.id} type="button" aria-pressed={caseId === item.id} aria-label={item.label} onClick={() => setCaseId(item.id)}>
                  {item.chip}
                </Chip>
              ))}
            </Chips>
            <CaseStack>
              {CASES.map((item) => (
                <Compare
                  key={item.id}
                  hidden={caseId !== item.id}
                  caption={item.caption}
                  before={item.before}
                  after={item.after}
                  beforeAlt={item.beforeAlt}
                  afterAlt={item.afterAlt}
                />
              ))}
            </CaseStack>
            </Report>
            </ReportFloat>
          </ReportSlot>
        </HeroInner>
        <DownRow>
          <Down href="#worry">
            아래로 보기
            <Mouse aria-hidden="true">
              <i data-motion="bob" />
            </Mouse>
          </Down>
        </DownRow>
      </Hero>

      <IndustryRow aria-label="업종 바로가기">
        <IndustryInner>
          <IndustryTitle>어떤 매장을 운영하세요?</IndustryTitle>
          <IndustryList>
            {INDUSTRIES.map((item) => (
              <IndustryChip key={item.key} to={item.path}>
                <img src={industryIcon(item.key)} alt="" width="40" height="40" />
                {item.name}
              </IndustryChip>
            ))}
          </IndustryList>
        </IndustryInner>
      </IndustryRow>

      <JumpNav aria-label="구간 바로가기">
        <JumpInner>
          {JUMPS.map((item) => (
            <JumpLink key={item.href} href={item.href}>
              {item.label}
            </JumpLink>
          ))}
        </JumpInner>
      </JumpNav>

      {brochureOpen && <BrochureDialog onClose={() => setBrochureOpen(false)} />}
    </>
  )
}

function Compare({
  hidden,
  caption,
  before,
  after,
  beforeAlt,
  afterAlt,
}: {
  hidden: boolean
  caption: string
  before: string
  after: string
  beforeAlt: string
  afterAlt: string
}) {
  const frameRef = useRef<HTMLDivElement>(null)
  const afterRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLSpanElement>(null)
  const rangeRef = useRef<HTMLInputElement>(null)
  const reduce = usePrefersReducedMotion()

  useEffect(() => {
    if (hidden) return
    const el = frameRef.current
    const top = afterRef.current
    const hd = handleRef.current
    const rg = rangeRef.current
    if (!el || !top || !rg) return

    let cur = Number(rg.value || 50)
    const started = Date.now()
    let visible = true
    let held = false
    let timer = 0

    const setX = (value: number) => {
      cur = value
      top.style.clipPath = `inset(0 0 0 ${value}%)`
      if (hd) hd.style.left = `${value}%`
      rg.value = String(value)
    }
    const onInput = () => {
      setX(Number(rg.value))
    }
    const hold = () => {
      held = true
    }
    const release = () => {
      held = false
    }

    rg.addEventListener('input', onInput)
    rg.addEventListener('focus', hold)
    rg.addEventListener('blur', release)
    el.addEventListener('mouseenter', hold)
    el.addEventListener('mouseleave', release)
    setX(cur)

    let observer: IntersectionObserver | undefined
    if (!reduce && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting
      })
      observer.observe(el)
    }

    const loop = () => {
      if (!el.isConnected) return
      if (visible && !held) {
        const k = ((Date.now() - started) % 6400) / 6400
        const target = 50 + 45 * Math.sin(k * 2 * Math.PI)
        setX(Number((cur + (target - cur) * 0.12).toFixed(2)))
      }
      timer = window.setTimeout(loop, 33)
    }
    if (!reduce) loop()

    return () => {
      window.clearTimeout(timer)
      observer?.disconnect()
      rg.removeEventListener('input', onInput)
      rg.removeEventListener('focus', hold)
      rg.removeEventListener('blur', release)
      el.removeEventListener('mouseenter', hold)
      el.removeEventListener('mouseleave', release)
    }
  }, [hidden, reduce])

  return (
    <CompareCard hidden={hidden}>
      <CompareHead>
        <Check aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Check>
        <div>
          <b>관리 완료 보고</b>
          <span>{caption}</span>
        </div>
      </CompareHead>
      <Frame ref={frameRef}>
        <img src={before} alt={beforeAlt} />
        <Tag $left>관리 전</Tag>
        <After ref={afterRef}>
          <img src={after} alt={afterAlt} draggable={false} />
        </After>
        <Tag>관리 후</Tag>
        <Handle ref={handleRef} aria-hidden="true">
          <Knob>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M9 6l-6 6 6 6M15 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Knob>
        </Handle>
        <Range ref={rangeRef} type="range" min={0} max={100} step={0.5} defaultValue={50} aria-label="관리 전후 비교" />
      </Frame>
    </CompareCard>
  )
}

function BrochureDialog({ onClose }: { onClose: () => void }) {
  const { mutateAsync, isLoading } = usePostSlackMessageSend()
  const [store, setStore] = useState('')
  const [email, setEmail] = useState('')
  const [agree, setAgree] = useState(false)
  const [touched, setTouched] = useState(false)
  const [failed, setFailed] = useState(false)
  const [done, setDone] = useState(false)
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())
  const emailErr = touched && !!email && !emailOk
  const ready = !!store.trim() && emailOk && agree

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (isLoading) return
    setTouched(true)
    setFailed(false)
    if (!ready) return
    try {
      await mutateAsync({
        template: 'SERVICE_INTRODUCE',
        data: { name: store.trim(), email: email.trim() },
      })
      setDone(true)
    } catch {
      setFailed(true)
    }
  }

  return (
    <ModalRoot>
      <Backdrop onClick={onClose} />
      <Dialog role="dialog" aria-modal="true" aria-labelledby="brochure-title">
        <Close type="button" aria-label="닫기" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        </Close>
        {done ? (
          <Done role="status">
            <DoneMark aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </DoneMark>
            <h2 id="brochure-title">소개서 신청이 완료됐어요</h2>
            <p>
              <b>{email.trim()}</b>로
              <br />
              24시간 이내에 보내 드릴게요. 감사합니다.
            </p>
            <DoneClose type="button" onClick={onClose}>
              확인
            </DoneClose>
          </Done>
        ) : (
        <Form noValidate onSubmit={onSubmit}>
          <Kicker>SERVICE INTRODUCTION</Kicker>
          <h2 id="brochure-title">소개서 받기</h2>
          <p>소개서를 전달받으실 간단한 정보를 입력해 주세요.</p>
          <input aria-label="브랜드 또는 매장명" autoComplete="organization" placeholder="브랜드 또는 매장명" value={store} onChange={(event) => setStore(event.target.value)} />
          <input
            type="email"
            aria-label="이메일"
            autoComplete="email"
            placeholder="이메일"
            value={email}
            aria-invalid={emailErr}
            onBlur={() => setTouched(true)}
            onChange={(event) => setEmail(event.target.value)}
          />
          {emailErr && <Alert>이메일 주소를 다시 확인해 주세요.</Alert>}
          <Agree>
            <label>
              <input type="checkbox" checked={agree} onChange={(event) => setAgree(event.target.checked)} />
              개인정보 수집 및 이용에 동의합니다.
            </label>
            <small>
              수집 항목: 매장명(브랜드명), 이메일
              <br />
              목적: 서비스 소개서 발송 및 도입 상담
              <br />
              보관 기간: 정보 기입 후 6개월
            </small>
          </Agree>
          {failed && <Fail>신청이 전송되지 않았어요. 잠시 후 다시 시도하거나 02-508-0913으로 전화 주세요.</Fail>}
          <FormActions>
            <Cancel type="button" onClick={onClose}>
              취소
            </Cancel>
            <Submit type="submit" disabled={!ready}>
              {isLoading ? '보내는 중이에요' : failed ? '다시 보내기' : '소개서 받기'}
            </Submit>
          </FormActions>
        </Form>
        )}
      </Dialog>
    </ModalRoot>
  )
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false)
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduce(media.matches)
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])
  return reduce
}

function Arrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  background: #081430;
  color: #ffffff;
  min-height: max(680px, calc(100svh - 113px));
  display: flex;
  flex-direction: column;
`

const Slides = styled.div`
  position: absolute;
  inset: -14px;
`

const Slide = styled.img<{ $on: boolean; $pos: string }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ $pos }) => $pos};
  display: block;
  opacity: ${({ $on }) => ($on ? 1 : 0)};
  transition: opacity 0.8s ease;
`

const Shade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(8, 20, 48, 0.94) 0%, rgba(8, 20, 48, 0.74) 46%, rgba(8, 20, 48, 0.32) 100%),
    linear-gradient(180deg, rgba(8, 20, 48, 0.5) 0%, rgba(8, 20, 48, 0) 30%, rgba(8, 20, 48, 0.9) 100%);
`

const Glow = styled.div`
  position: absolute;
  right: -12vw;
  top: -18vw;
  width: 52vw;
  height: 52vw;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(61, 90, 254, 0.38) 0%, rgba(61, 90, 254, 0) 68%);
  pointer-events: none;
`

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(64px, 8vw, 120px) clamp(20px, 3.4vw, 40px) clamp(32px, 4vw, 56px);
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(40px, 5vw, 80px);
`

const Copy = styled.div`
  flex: 1.4 1 520px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 26px;
`

const Eyebrow = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: clamp(20px, 1.9vw, 26px);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: rgba(255, 255, 255, 0.8);
`

const WordPill = styled.span`
  display: inline-grid;
  align-items: center;
  height: 1.9em;
  padding: 0 0.7em;
  border-radius: 12px;
  background: #ffffff;
  color: #0c2049;
  overflow: hidden;
`

const Word = styled.span`
  grid-area: 1 / 1;
  white-space: nowrap;
  opacity: 0;
  transform: translateY(100%);
  &:first-child {
    opacity: 1;
    transform: none;
  }
`

const Title = styled.h1`
  margin: 0;
  font-size: clamp(38px, 5.4vw, 76px);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.045em;
`

const TitleLine = styled.span`
  display: block;
  overflow: hidden;
  padding-bottom: 0.08em;
  margin-bottom: -0.08em;
`

const TitleText = styled.span`
  display: inline-block;
`

const Amber = styled.span`
  color: #ffb649;
`

const Lead = styled.span`
  display: inline-block;
  font-size: clamp(16px, 1.4vw, 20px);
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.5;
  margin-top: 0.4em;
  color: rgba(255, 255, 255, 0.88);
`

const Description = styled.p`
  margin: 14px 0 0;
  max-width: 44ch;
  font-size: clamp(17px, 1.4vw, 19px);
  font-weight: 400;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.86);
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

const buttonFace = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  white-space: nowrap;
  letter-spacing: -0.02em;
  transition: background-color 0.2s, transform 0.15s;
`

const Primary = styled(Link)`
  ${buttonFace};
  gap: 8px;
  min-height: 60px;
  padding: 0 30px;
  border-radius: 16px;
  background: #ffb649;
  color: #191f28;
  font-size: 18px;
  font-weight: 800;
  box-shadow: 0 18px 40px -16px rgba(255, 182, 73, 0.7);

  &:hover {
    background: #ffc46b;
    transform: translateY(-2px);
  }
`

const glassFace = css`
  ${buttonFace};
  min-height: 60px;
  padding: 0 28px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1.4px solid rgba(255, 255, 255, 0.4);
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`

const Ghost = styled(Link)`
  ${glassFace};
`

const GhostButton = styled.button`
  ${glassFace};
  font-family: inherit;
  cursor: pointer;
`

const PriceLink = styled.a`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  margin-top: -6px;
  font-size: 16px;
  font-weight: 700;
  text-underline-offset: 4px;

  && {
    color: rgba(255, 255, 255, 0.86);
    text-decoration: underline;
  }

  &&:hover {
    color: #ffffff;
  }
`

const cardBob = keyframes`
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0);
  }
`

const ReportSlot = styled.div`
  flex: 0 1 360px;
  min-width: 0;
  margin: 0 auto;
`

const ReportFloat = styled.div``

const Report = styled.div`
  border-radius: 28px;
  padding: 14px;
  background: rgba(22, 40, 86, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 40px 80px -30px rgba(0, 0, 0, 0.6);
  color: #ffffff;
  animation: ${cardBob} 5200ms ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Chips = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  padding: 2px 2px 12px;
`

const Chip = styled.button`
  min-height: 44px;
  padding: 0 4px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background-color 0.25s, color 0.25s, border-color 0.25s;

  &[aria-pressed='true'] {
    border-color: #ffb649;
    background: #ffb649;
    color: #191f28;
  }
`

const CaseStack = styled.div`
  display: grid;
`

const CompareCard = styled.div`
  grid-area: 1 / 1;

  &[hidden] {
    display: none;
  }
`

const CompareHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 6px 14px;

  div {
    display: flex;
    flex-direction: column;
    line-height: 1.35;
  }

  b {
    font-size: 16px;
    font-weight: 800;
  }

  div > span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
  }
`

const Check = styled.span`
  && {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    background: #ffb649;
    color: #191f28;
    display: grid;
    place-items: center;
  }
`

const Frame = styled.div`
  position: relative;
  aspect-ratio: 4 / 5;
  border-radius: 18px;
  overflow: hidden;
  user-select: none;
  background: #0c2049;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const After = styled.div`
  position: absolute;
  inset: 0;
  clip-path: inset(0 0 0 50%);
`

const Tag = styled.span<{ $left?: boolean }>`
  position: absolute;
  top: 12px;
  ${({ $left }) => ($left ? 'left: 12px;' : 'right: 12px;')}
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ $left }) => ($left ? '#ffffff' : '#003399')};
  color: ${({ $left }) => ($left ? '#191f28' : '#ffffff')};
  font-size: 14px;
  font-weight: 800;
  pointer-events: none;
`

const Handle = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 3px;
  margin-left: -1.5px;
  background: #ffffff;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.25);
  pointer-events: none;
`

const Knob = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  border-radius: 50%;
  background: #ffffff;
  color: #003399;
  display: grid;
  place-items: center;
  box-shadow: 0 6px 16px -6px rgba(0, 0, 0, 0.4);
`

const Range = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: ew-resize;
`

const DownRow = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 clamp(20px, 3.4vw, 40px) 22px;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`

const Down = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  color: rgba(255, 255, 255, 0.86);
  font-size: 14px;
  font-weight: 700;
`

const Mouse = styled.span`
  width: 26px;
  height: 40px;
  border-radius: 13px;
  border: 1.6px solid rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  padding-top: 7px;
  box-sizing: border-box;

  i {
    width: 4px;
    height: 8px;
    border-radius: 2px;
    background: #ffffff;
  }
`

const IndustryRow = styled.section`
  background: #ffffff;
  border-bottom: 1px solid #e5e8eb;
`

const IndustryInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 20px clamp(20px, 3.4vw, 40px) 24px;
  box-sizing: border-box;
`

const IndustryTitle = styled.p`
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 800;
  color: #191f28;
`

const IndustryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 96px), 1fr));
  gap: 8px;
`

const IndustryChip = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  aspect-ratio: 1 / 1;
  padding: 8px;
  border-radius: 16px;
  background: #f2f4f6;
  color: #191f28;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  box-sizing: border-box;
  transition: background-color 0.2s, color 0.2s;

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    display: block;
  }

  &:hover {
    background: #eef2ff;
    color: #003399;
  }
`

const JumpNav = styled.nav`
  position: sticky;
  top: 68px;
  z-index: 30;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #e5e8eb;
  backdrop-filter: blur(8px);
`

const JumpInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 clamp(8px, 3.4vw, 40px);
  box-sizing: border-box;
  display: flex;
  gap: 2px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const JumpLink = styled.a`
  flex: none;
  display: inline-flex;
  align-items: center;
  min-height: 52px;
  padding: 0 14px;
  font-size: 15px;
  font-weight: 700;
  color: #4e5968;
  white-space: nowrap;

  &:hover {
    color: #003399;
  }
`

const ModalRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 90;
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 20, 30, 0.55);
`

const Dialog = styled.div`
  position: fixed;
  z-index: 91;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 520px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: clamp(28px, 5vw, 44px) clamp(22px, 5vw, 44px);
  border-radius: 28px;
  background: #ffffff;
  color: #191f28;
`

const Close = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: #f2f4f6;
  color: #191f28;
  display: grid;
  place-items: center;
  cursor: pointer;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;

  h2 {
    margin: 14px 0 0;
    font-size: clamp(26px, 3vw, 30px);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.3;
  }

  > p {
    margin: 12px 0 0;
    font-size: 17px;
    line-height: 1.6;
    color: #4e5968;
  }

  input[type='email'] {
    margin-top: 12px;
    width: 100%;
    height: 60px;
    padding: 0 20px;
    border-radius: 14px;
    border: 1.5px solid #e5e8eb;
    background: #ffffff;
    font: inherit;
    font-size: 17px;
    color: #191f28;
    box-sizing: border-box;

    &[aria-invalid='true'] {
      border-color: #c62828;
    }
  }

  input:not([type='email']):not([type='checkbox']) {
    margin-top: 28px;
    width: 100%;
    height: 60px;
    padding: 0 20px;
    border-radius: 14px;
    border: 1.5px solid #e5e8eb;
    background: #ffffff;
    font: inherit;
    font-size: 17px;
    color: #191f28;
    box-sizing: border-box;
  }
`

const Kicker = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: #6b7684;
`

const Agree = styled.div`
  margin-top: 16px;
  padding: 20px;
  border-radius: 16px;
  background: #f2f4f6;

  label {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 700;
  }

  input {
    appearance: none;
    flex: none;
    width: 22px;
    height: 22px;
    margin: 0;
    border: 1.5px solid #d1d6db;
    border-radius: 6px;
    background: #ffffff;
    display: grid;
    place-items: center;

    &:checked {
      border-color: #003399;
      background: #003399;
    }

    &:checked::after {
      content: '';
      width: 5px;
      height: 9px;
      margin-top: -2px;
      border: solid #ffffff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  small {
    display: block;
    margin-top: 12px;
    padding-top: 14px;
    border-top: 1px solid #e5e8eb;
    font-size: 14px;
    line-height: 1.8;
    color: #4e5968;
  }
`

const Alert = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #c62828;
`

const Fail = styled.p`
  margin: 16px 0 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fdecec;
  color: #9b1c1c;
  font-size: 14px;
  font-weight: 600;
`

const FormActions = styled.div`
  margin-top: 28px;
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  gap: 12px;
`

const Cancel = styled.button`
  min-height: 60px;
  border: 0;
  border-radius: 999px;
  background: #e5e8eb;
  color: #191f28;
  font: inherit;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: #d1d6db;
  }
`

const Done = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 16px;

  h2 {
    margin: 24px 0 0;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    font-size: clamp(24px, 3vw, 28px);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.35;
  }

  p {
    margin: 14px 0 0;
    font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
    font-size: 17px;
    font-weight: 400;
    line-height: 1.7;
    color: #4e5968;
  }

  b {
    font-weight: 800;
    color: #191f28;
    word-break: break-all;
  }
`

const DoneMark = styled.span`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #ffb649;
  color: #191f28;
  display: grid;
  place-items: center;
`

const DoneClose = styled.button`
  margin-top: 32px;
  width: 100%;
  min-height: 60px;
  border: 0;
  border-radius: 999px;
  background: #0c2049;
  color: #ffffff;
  font-family: Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.15;
  cursor: pointer;

  &:hover {
    background: #16336e;
  }
`

const Submit = styled.button`
  min-height: 60px;
  border: 0;
  border-radius: 999px;
  background: #003399;
  color: #ffffff;
  font: inherit;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    background: #e5e8eb;
    color: #8b95a1;
    cursor: not-allowed;
  }
`
